import { DailyOpeningHours, Weekday, WeeklyOpeningHours } from '@shared/models';

export interface StoreStatus {
  isOpenToday: boolean;
  isOpenNow: boolean;
  /** Raw object for today */
  today?: DailyOpeningHours;
  /** Human-readable string of today’s opening slots */
  hoursToday?: string;
  nextOpening?: Date;
  nextClosing?: Date;
  errors: string[];
}

// --- Internal types ---
type ParsedSlot = { start: number; end: number };
type ParsedDay = {
  isOpen: boolean;
  isExceptionalClosure?: boolean;
  slots: ParsedSlot[];
};
type ParsedWeekly = Record<Weekday, ParsedDay>;

// --- Constants ---
const WEEK_DAYS: Weekday[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

// --- Helpers ---

/** Parse "HH:mm" into minutes-since-midnight, or null if invalid */
function parseTime(t: string): number | null {
  const parts = t.split(':');
  if (parts.length !== 2) return null;
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  if (Number.isInteger(h) && Number.isInteger(m) && h >= 0 && h <= 23 && m >= 0 && m <= 59) {
    return h * 60 + m;
  }
  return null;
}

/** Build or retrieve from cache a ParsedWeekly */
function getParsedWeekly(
  weekly: WeeklyOpeningHours,
  cache: WeakMap<WeeklyOpeningHours, ParsedWeekly>,
  errors: string[]
): ParsedWeekly {
  if (cache.has(weekly)) {
    return cache.get(weekly)!;
  }

  const parsed = {} as ParsedWeekly;
  for (const day of WEEK_DAYS) {
    const dh = weekly[day];
    if (!dh) {
      errors.push(`Missing data for ${day}`);
      continue;
    }
    const slots: ParsedSlot[] = [];
    if (dh.isOpen) {
      for (const { start, end } of dh.timeSlots) {
        const s = parseTime(start);
        const e = parseTime(end);
        if (s === null || e === null) {
          errors.push(`Bad format ${start}-${end} on ${day}`);
        } else if (s >= e) {
          errors.push(`Invalid range ${start}-${end} on ${day}`);
        } else {
          slots.push({ start: s, end: e });
        }
      }
    }
    parsed[day] = {
      isOpen: dh.isOpen,
      isExceptionalClosure: dh.isExceptionalClosure,
      slots,
    };
  }

  cache.set(weekly, parsed);
  return parsed;
}

/** Compute isOpenToday, isOpenNow, and currentSlotEnd */
function computeTodayStatus(
  parsed: ParsedWeekly,
  todayKey: Weekday,
  nowMin: number,
  errors: string[]
): { isOpenToday: boolean; isOpenNow: boolean; currentSlotEnd: number | null } {
  const day = parsed[todayKey];
  if (!day) {
    errors.push(`No parsed entry for ${todayKey}`);
    return { isOpenToday: false, isOpenNow: false, currentSlotEnd: null };
  }
  if (!day.isOpen) {
    return { isOpenToday: false, isOpenNow: false, currentSlotEnd: null };
  }
  for (const { start, end } of day.slots) {
    if (nowMin >= start && nowMin < end) {
      return { isOpenToday: true, isOpenNow: true, currentSlotEnd: end };
    }
  }
  return { isOpenToday: true, isOpenNow: false, currentSlotEnd: null };
}

/** Find nextOpening and nextClosing over the week */
function computeNextSlots(
  parsed: ParsedWeekly,
  date: Date,
  todayKey: Weekday,
  nowMin: number,
  isOpenNow: boolean,
  currentSlotEnd: number | null
): { nextOpening?: Date; nextClosing?: Date } {
  let nextOpening: Date | undefined;
  let nextClosing: Date | undefined;

  if (isOpenNow && currentSlotEnd !== null) {
    nextClosing = new Date(date);
    nextClosing.setHours(Math.floor(currentSlotEnd / 60), currentSlotEnd % 60, 0, 0);
  }

  // future slots today
  const todaySlots = parsed[todayKey]?.slots ?? [];
  const future = todaySlots.find((s) => s.start > nowMin);
  if (future) {
    nextOpening = new Date(date);
    nextOpening.setHours(Math.floor(future.start / 60), future.start % 60, 0, 0);
    if (!nextClosing) {
      nextClosing = new Date(date);
      nextClosing.setHours(Math.floor(future.end / 60), future.end % 60, 0, 0);
    }
    return { nextOpening, nextClosing };
  }

  // scan following days
  for (let offset = 1; offset <= 7; offset++) {
    const idx = (date.getDay() + offset) % 7;
    const key = WEEK_DAYS[idx] as Weekday;
    const slots = parsed[key]?.slots;
    if (slots && slots.length > 0) {
      const first = slots[0]!;
      nextOpening = new Date(date);
      nextOpening.setDate(date.getDate() + offset);
      nextOpening.setHours(Math.floor(first.start / 60), first.start % 60, 0, 0);
      if (!nextClosing) {
        nextClosing = new Date(nextOpening);
        nextClosing.setHours(Math.floor(first.end / 60), first.end % 60, 0, 0);
      }
      break;
    }
  }

  return { nextOpening, nextClosing };
}

// --- Main factory ---
export const getStoreStatus = (() => {
  const parsedCache = new WeakMap<WeeklyOpeningHours, ParsedWeekly>();
  const statusCache = new Map<string, StoreStatus>();

  return (weekly: WeeklyOpeningHours, date: Date = new Date()): StoreStatus => {
    const errors: string[] = [];
    const minuteKey = date.toISOString().slice(0, 16);
    const cacheKey = `${JSON.stringify(weekly)}:${minuteKey}`;

    if (statusCache.has(cacheKey)) {
      return statusCache.get(cacheKey)!;
    }

    const parsed = getParsedWeekly(weekly, parsedCache, errors);
    const todayKey = WEEK_DAYS[date.getDay()] as Weekday;
    const nowMin = date.getHours() * 60 + date.getMinutes();

    // 1. Statut du jour
    const { isOpenToday, isOpenNow, currentSlotEnd } = computeTodayStatus(parsed, todayKey, nowMin, errors);

    // 2. Prochaines ouvertures/fermetures
    const { nextOpening, nextClosing } = computeNextSlots(parsed, date, todayKey, nowMin, isOpenNow, currentSlotEnd);

    // 3. Format des horaires du jour
    let formattedHours: string | undefined;
    const todayRaw = weekly[todayKey];
    if (todayRaw?.isOpen && todayRaw.timeSlots.length > 0) {
      formattedHours = todayRaw.timeSlots.map((slot) => `${slot.start} : ${slot.end}`).join(' - ');
    }

    const result: StoreStatus = {
      isOpenToday,
      isOpenNow,
      today: todayRaw,
      hoursToday: formattedHours,
      nextOpening,
      nextClosing,
      errors,
    };

    statusCache.set(cacheKey, result);
    return result;
  };
})();
